interface EventEmitter<N, E> {
  addEventListener: (name: N, listener: (event: E) => void) => void;
  removeEventListener: (name: N, listener: (event: E) => void) => void;
}

/**
 * Utility class to make it easy to remove all listeners added to a given target.
 *
 * Whenever we register a listener on a target, it's best practice to remove the
 * listener at some future point in time when we dispose of the target.  Not
 * doing this is a good way to lead to memory leaks and weird behavior when
 * listeners fire when we don't expect them to.
 *
 * @example
 *     const listener = new Listener();
 *     // Register a listener on window
 *     listener.listenTo(window, 'resize', () => console.log('Window was resized'));
 *     // Remove all listeners we've added to window
 *     listener.stopListeningTo(window);
 */
export class Listener {
  private listeners: { target: any; event: string; listener: any }[] = [];

  /**
   * Register a listener on the target.
   */
  listenTo<N extends string, E>(
    target: EventEmitter<N, E>,
    event: N,
    listener: (event: E) => void,
  ): void {
    // TODO: Would be nice if this was more type safe...
    target.addEventListener(event, listener);
    this.listeners.push({ target, event, listener });
  }

  /**
   * Remove all listeners from a single target.
   */
  stopListeningTo(target: EventEmitter<string, any>): void {
    for (const listener of this.listeners) {
      if (listener.target === target) {
        target.removeEventListener(listener.event, listener.listener);
      }
    }

    this.listeners = this.listeners.filter((l) => l.target !== target);
  }

  /**
   * Remove all listeners from all targets.
   */
  stopListening(): void {
    for (const listener of this.listeners) {
      listener.target.removeEventListener(listener.event, listener.listener);
    }

    this.listeners = [];
  }
}
